import React, { useState } from "react";
import { Listings } from "../lib/api";
import { ArrowRight, ArrowLeft, CheckCircle2, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const initial = {
  role: "Property Owner",
  first_name: "", last_name: "", email: "", phone: "",
  tenant_name: "", tenant_industry: "",
  lease_period: "", lockin_period: "",
  floor: "",
  city: "", area: "", building_name: "", shop_number: "",
  property_type: "Showroom",
  super_built_up_area: "", carpet_area: "",
  lease_start_date: "",
  security_deposit: "", escalation_every_years: "", rent_escalation_percent: "",
  asking_price: "", monthly_rent: "",
  type_of_listing: "Sale",
  property_tax_paid_by: "Landlord",
  maintenance_paid_by: "Tenant",
  comments: "",
  image_url: "",
};

export default function ListYourProperty() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => {
    if (step === 1) {
      const req = ["first_name", "last_name", "email", "tenant_name", "city", "area", "building_name", "property_type"];
      for (const f of req) if (!data[f]) { toast.error(`${f.replace(/_/g, " ")} is required`); return; }
    }
    setStep((s) => s + 1);
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        super_built_up_area: data.super_built_up_area ? parseFloat(data.super_built_up_area) : null,
        carpet_area: data.carpet_area ? parseFloat(data.carpet_area) : null,
        security_deposit: data.security_deposit ? parseFloat(data.security_deposit) : null,
        escalation_every_years: data.escalation_every_years ? parseInt(data.escalation_every_years) : null,
        rent_escalation_percent: data.rent_escalation_percent ? parseFloat(data.rent_escalation_percent) : null,
        asking_price: data.asking_price ? parseFloat(data.asking_price) : null,
        monthly_rent: data.monthly_rent ? parseFloat(data.monthly_rent) : null,
        role: data.role.toLowerCase().includes("developer") ? "developer" : "owner",
      };
      await Listings.create(payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="container-zelo py-20 max-w-2xl text-center" data-testid="thank-you-page">
        <div className="w-20 h-20 rounded-full bg-brand-50 text-brand mx-auto flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight">Thank you. We're on it.</h1>
        <p className="mt-4 text-neutral-500 text-lg">Our acquisition team will review your property and reach out within 24-48 hours. Verified, leased properties get prioritized listing.</p>
        <a href="/properties" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:bg-brand-600 transition shadow-brand">
          Explore Properties <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    );
  }

  return (
    <div className="container-zelo py-12 md:py-20" data-testid="list-property-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="label-tiny text-brand">Zelo Listing Desk</div>
          <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-3 text-balance">List your verified, rent-ready property.</h1>
          <p className="mt-4 text-neutral-500 flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Zelo lists only verified, rent-ready properties.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-3 mb-10" data-testid="stepper">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition ${step >= s ? "bg-brand text-white" : "bg-neutral-100 text-neutral-400"}`}>{s}</div>
              {s !== 3 && <div className={`w-12 h-px ${step > s ? "bg-brand" : "bg-neutral-200"}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white p-6 md:p-10">
          {step === 1 && (
            <div className="space-y-5" data-testid="step-1-property">
              <SectionTitle>Tell us about you & the property</SectionTitle>

              <Field label="You are a">
                <select value={data.role} onChange={(e) => set("role", e.target.value)} className={inputCls} data-testid="form-role">
                  <option>Property Owner</option>
                  <option>Developer</option>
                </select>
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="First Name *"><input className={inputCls} value={data.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="form-first-name" /></Field>
                <Field label="Last Name *"><input className={inputCls} value={data.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="form-last-name" /></Field>
                <Field label="Email *"><input type="email" className={inputCls} value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="form-email" /></Field>
                <Field label="Phone"><input className={inputCls} value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid="form-phone" /></Field>
                <Field label="Tenant Name *"><input className={inputCls} value={data.tenant_name} onChange={(e) => set("tenant_name", e.target.value)} data-testid="form-tenant-name" /></Field>
                <Field label="Tenant Industry"><input className={inputCls} placeholder="e.g. Banking, Jewelry" value={data.tenant_industry} onChange={(e) => set("tenant_industry", e.target.value)} data-testid="form-tenant-industry" /></Field>
                <Field label="Lease Period (years)"><input className={inputCls} value={data.lease_period} onChange={(e) => set("lease_period", e.target.value)} data-testid="form-lease-period" /></Field>
                <Field label="Lock-in Period (years)"><input className={inputCls} value={data.lockin_period} onChange={(e) => set("lockin_period", e.target.value)} data-testid="form-lockin-period" /></Field>
                <Field label="Floor"><input className={inputCls} value={data.floor} onChange={(e) => set("floor", e.target.value)} data-testid="form-floor" /></Field>
                <Field label="Property Type">
                  <select value={data.property_type} onChange={(e) => set("property_type", e.target.value)} className={inputCls} data-testid="form-property-type">
                    {["Showroom", "Bank", "QSR", "Retail", "F&B", "Office", "Warehouse"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="City *"><input className={inputCls} value={data.city} onChange={(e) => set("city", e.target.value)} data-testid="form-city" /></Field>
                <Field label="Area *"><input className={inputCls} value={data.area} onChange={(e) => set("area", e.target.value)} data-testid="form-area" /></Field>
                <Field label="Building Name *"><input className={inputCls} value={data.building_name} onChange={(e) => set("building_name", e.target.value)} data-testid="form-building-name" /></Field>
                <Field label="Shop / Unit Number"><input className={inputCls} value={data.shop_number} onChange={(e) => set("shop_number", e.target.value)} data-testid="form-shop-number" /></Field>
                <Field label="Super Built Up Area (sqft)"><input className={inputCls} value={data.super_built_up_area} onChange={(e) => set("super_built_up_area", e.target.value)} data-testid="form-sba" /></Field>
                <Field label="Carpet Area (sqft)"><input className={inputCls} value={data.carpet_area} onChange={(e) => set("carpet_area", e.target.value)} data-testid="form-carpet" /></Field>
              </div>

              <div className="flex justify-end pt-2">
                <button onClick={next} className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95" data-testid="step-1-next">
                  Proceed to Lease Info <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="step-2-lease">
              <SectionTitle>Lease & financial details</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Lease Start Date"><input type="date" className={inputCls} value={data.lease_start_date} onChange={(e) => set("lease_start_date", e.target.value)} data-testid="form-lease-start" /></Field>
                <Field label="Security Deposit (₹)"><input className={inputCls} value={data.security_deposit} onChange={(e) => set("security_deposit", e.target.value)} data-testid="form-deposit" /></Field>
                <Field label="Escalation Every (years)"><input className={inputCls} value={data.escalation_every_years} onChange={(e) => set("escalation_every_years", e.target.value)} data-testid="form-escalate-every" /></Field>
                <Field label="Rent Escalation (%)"><input className={inputCls} value={data.rent_escalation_percent} onChange={(e) => set("rent_escalation_percent", e.target.value)} data-testid="form-escalation-percent" /></Field>
                <Field label="Asking Price (₹)"><input className={inputCls} value={data.asking_price} onChange={(e) => set("asking_price", e.target.value)} data-testid="form-asking" /></Field>
                <Field label="Monthly Rent (₹)"><input className={inputCls} value={data.monthly_rent} onChange={(e) => set("monthly_rent", e.target.value)} data-testid="form-rent" /></Field>
                <Field label="Type of Listing">
                  <select className={inputCls} value={data.type_of_listing} onChange={(e) => set("type_of_listing", e.target.value)} data-testid="form-listing-type">
                    {["Sale", "Sale & Leaseback", "Other"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Property Tax Paid By">
                  <select className={inputCls} value={data.property_tax_paid_by} onChange={(e) => set("property_tax_paid_by", e.target.value)} data-testid="form-tax-paidby">
                    {["Landlord", "Tenant"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Maintenance Paid By">
                  <select className={inputCls} value={data.maintenance_paid_by} onChange={(e) => set("maintenance_paid_by", e.target.value)} data-testid="form-maint-paidby">
                    {["Landlord", "Tenant"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Comments"><textarea rows={4} className={inputCls} value={data.comments} onChange={(e) => set("comments", e.target.value)} data-testid="form-comments" /></Field>

              <div className="flex justify-between pt-2">
                <button onClick={back} className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-5 py-3 text-sm font-semibold hover:border-brand hover:text-brand transition" data-testid="step-2-back">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={next} className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95" data-testid="step-2-next">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="step-3-image">
              <SectionTitle>Upload property image (optional)</SectionTitle>
              <Field label="Image URL"><input className={inputCls} placeholder="https://…" value={data.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="form-image-url" /></Field>
              <div className="rounded-xl border-2 border-dashed border-neutral-200 p-8 text-center text-neutral-400">
                <Upload className="w-6 h-6 mx-auto mb-2" />
                <div className="text-sm">For now, please paste a public image URL above. (File upload coming soon.)</div>
              </div>
              <div className="flex justify-between pt-2">
                <button onClick={back} className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-5 py-3 text-sm font-semibold hover:border-brand hover:text-brand transition" data-testid="step-3-back">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={submit} disabled={loading} className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95 disabled:opacity-60" data-testid="step-3-submit">
                  {loading ? "Submitting…" : "Submit Listing"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-xl border border-neutral-200 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-sm bg-white";

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-neutral-600 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="font-heading text-xl md:text-2xl font-medium tracking-tight mb-2">{children}</div>;
}
